// Platform-agnostic notification decision core. Holds the WhatsApp/Slack-style
// routing rules; web and desktop inject the I/O (banner / OS notification /
// sound / badge) and ambient-state accessors via `NotificationServiceDeps`.
//
// Routing summary (see `handle`):
//   - self / master-off            → nothing
//   - mention                      → always alert (bypasses mute + DND)
//   - muted conversation / in DND  → badge only, no alert
//   - focused + viewing that chat  → badge only (unless notifyOnlyWhenUnfocused=false)
//   - focused + a different chat   → in-app banner (+ chime)
//   - unfocused / backgrounded     → OS notification (+ chime) + badge
//
// All preview text is built here from already-decrypted, client-side data and
// redacted per the `notificationPreview` setting.

import type {
  BannerPresentation,
  IncomingNotification,
  NotificationPreferences,
  NotificationService,
  NotificationServiceDeps,
  NotificationSound,
  OsNotificationPresentation,
} from './types';

let bannerSeq = 0;
function nextBannerId(): string {
  bannerSeq += 1;
  return `ntf-${bannerSeq}`;
}

/** Parse "HH:mm" → minutes since midnight, or null if malformed. */
function parseHhMm(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** True when `nowMs` (local time) falls inside the quiet-hours window.
 *  Handles windows that wrap past midnight (e.g. 22:00 → 08:00). */
export function isWithinQuietHours(
  prefs: NotificationPreferences,
  nowMs: number,
): boolean {
  if (!prefs.dndEnabled) return false;
  const start = parseHhMm(prefs.dndStart);
  const end = parseHhMm(prefs.dndEnd);
  if (start === null || end === null) return false;
  const d = new Date(nowMs);
  const cur = d.getHours() * 60 + d.getMinutes();
  if (start === end) return true; // full-day DND
  if (start < end) return cur >= start && cur < end;
  // Wraps midnight: inside if after start OR before end.
  return cur >= start || cur < end;
}

/** Build the (title, body) per the preview-redaction setting + event kind. */
function composeText(
  evt: IncomingNotification,
  preview: NotificationPreferences['notificationPreview'],
): { title: string; body: string } {
  const sender = evt.senderName?.trim() || undefined;
  const convo = evt.conversationName?.trim() || undefined;

  if (evt.kind === 'invite') {
    return {
      title: 'New chat',
      body: `${sender ?? convo ?? 'Someone'} started a conversation`,
    };
  }
  if (evt.kind === 'device') {
    return {
      title: 'New device linked',
      body: 'A new device was linked to your account.',
    };
  }
  if (evt.kind === 'profile') {
    return {
      title: 'Contact updated',
      body: `${sender ?? 'A contact'} updated their profile.`,
    };
  }

  // message / mention
  const title = convo ?? sender ?? 'Ping';
  if (preview === 'none') {
    return {
      title,
      body: evt.isMention ? 'Mentioned you' : 'New message',
    };
  }
  if (preview === 'name') {
    const who = sender ?? 'Someone';
    return {
      title,
      body: evt.isMention ? `${who} mentioned you` : `${who} sent a message`,
    };
  }
  // full
  const text = evt.preview?.trim() || (evt.isMention ? 'Mentioned you' : 'New message');
  // In a group, prefix the sender so "Alice: hey" reads naturally. For a 1:1
  // the title already IS the sender, so don't double it up.
  const showSenderPrefix = sender !== undefined && convo !== undefined && convo !== sender;
  const body = evt.isMention
    ? showSenderPrefix
      ? `${sender} mentioned you: ${text}`
      : `Mentioned you: ${text}`
    : showSenderPrefix
      ? `${sender}: ${text}`
      : text;
  return { title, body };
}

export function createNotificationService(
  deps: NotificationServiceDeps,
): NotificationService {
  const now = deps.now ?? Date.now;

  function handle(evt: IncomingNotification): void {
    const prefs = deps.getPreferences();

    // Never notify on our own messages (own-account multi-device echo).
    if (evt.senderId && deps.isOwnSender?.(evt.senderId)) return;

    // Master switch. Badge still reflects unread truth for chat events.
    const isChatEvent = evt.kind === 'message' || evt.kind === 'mention' || evt.kind === 'invite';
    if (!prefs.notificationsEnabled) {
      if (isChatEvent) deps.refreshBadge();
      return;
    }

    // Per-conversation notification level (all | mentions | off). 'off' is a
    // HARD silence — even an @mention only bumps the badge. This is checked
    // BEFORE the mention bypass below so "off" truly means off.
    const convLevel = evt.conversationId
      ? deps.getConversationNotificationLevel?.(evt.conversationId)
      : undefined;
    if (convLevel === 'off') {
      if (isChatEvent) deps.refreshBadge();
      return;
    }

    // Mentions are high-signal: they bypass mute + DND. Everything else is
    // subject to per-conversation mute/level and quiet hours (badge still
    // updates). 'mentions' level suppresses non-mentions just like a mute.
    if (!evt.isMention) {
      const suppressed =
        (evt.conversationId !== undefined && deps.isConversationMuted(evt.conversationId)) ||
        convLevel === 'mentions';
      if (suppressed) {
        if (isChatEvent) deps.refreshBadge();
        return;
      }
      if (isWithinQuietHours(prefs, now())) {
        if (isChatEvent) deps.refreshBadge();
        return;
      }
    }

    const visible = deps.getTabVisible();
    const windowFocused = deps.getWindowFocused();
    const foreground = visible && windowFocused;
    const active =
      evt.conversationId !== undefined &&
      deps.getActiveConversationId() === evt.conversationId;
    // "Viewing this chat" = that exact conversation is on screen AND the tab is
    // visible. We deliberately do NOT require OS window focus here: if you have
    // the chat open and the tab is on screen, a new message in THAT chat should
    // never raise a banner — even if focus is momentarily elsewhere (DevTools,
    // another window). It lands in the open thread directly.
    const viewingThis = visible && active;

    // Looking right at the conversation → just keep the badge in sync, no
    // banner/sound. EXCEPTION: an @mention always pings (the mention chime
    // still plays below) so a tag isn't missed while you're in the chat.
    if (viewingThis && prefs.notifyOnlyWhenUnfocused && !evt.isMention) {
      if (isChatEvent) deps.refreshBadge();
      return;
    }

    const sound: NotificationSound = evt.isMention ? 'mention' : 'message';
    const { title, body } = composeText(evt, prefs.notificationPreview);

    let alerted = false;

    // IN-APP BANNER: shown whenever the tab is VISIBLE and you're not actively
    // viewing that exact chat — regardless of OS window focus. This is the
    // surface you see when Ping is on screen (foreground, or visible behind
    // another app). A hidden tab can't show it, so it's skipped there.
    if (visible && !viewingThis) {
      const banner: BannerPresentation = {
        id: nextBannerId(),
        title,
        body,
        isMention: evt.isMention,
        ...(evt.conversationId !== undefined ? { conversationId: evt.conversationId } : {}),
        ...(evt.senderId !== undefined ? { senderId: evt.senderId } : {}),
      };
      deps.presentBanner(banner);
      alerted = true;
    }

    // OS NOTIFICATION: shown whenever the app is NOT in the foreground (hidden
    // tab OR another app/window on top), so the alert surfaces over whatever
    // you're doing. Overlaps the banner in the visible-but-unfocused case —
    // intentional, so the alert is never missed.
    if (!foreground && prefs.osNotificationsEnabled) {
      const os: OsNotificationPresentation = {
        title,
        body,
        tag: evt.conversationId ?? `ping-${evt.kind}`,
        ...(evt.conversationId !== undefined ? { conversationId: evt.conversationId } : {}),
      };
      deps.presentOsNotification(os);
      alerted = true;
    }

    // Chime when we alerted, or for an in-chat mention ping while viewing.
    if (prefs.inAppSounds && (alerted || (viewingThis && evt.isMention))) {
      deps.playSound(sound);
    }

    if (isChatEvent) deps.refreshBadge();
  }

  return { handle };
}
